import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { endpoints } from "@/api";
import { useAuth } from "@/auth";

/**
 * Track user activity:
 *  - Pageview on every route change
 *  - Click count on every interactive click (debounced into batches)
 *  - Heartbeat with elapsed time every 15 seconds while the tab is visible
 *
 * Tracking is no-op when there is no logged-in user.
 */
export function useActivityTracker() {
  const { user } = useAuth();
  const location = useLocation();
  const lastHeartbeat = useRef(0);
  const enabled = !!user;

  // Pageview on route change
  useEffect(() => {
    if (!enabled) return;
    const path = location.pathname;
    endpoints.trackEvent({ type: "pageview", path }).catch(() => {});
  }, [enabled, location.pathname]);

  // Heartbeat: send time-on-site every 15s while visible
  useEffect(() => {
    if (!enabled) return;
    if (!lastHeartbeat.current) lastHeartbeat.current = Date.now();
    const path = location.pathname;
    const beat = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      const elapsed = now - lastHeartbeat.current;
      lastHeartbeat.current = now;
      if (elapsed > 1000) {
        endpoints
          .trackEvent({ type: "heartbeat", path, duration_ms: Math.min(elapsed, 60_000) })
          .catch(() => {});
      }
    };
    const interval = setInterval(beat, 15_000);
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        lastHeartbeat.current = Date.now();
      } else {
        beat();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, location.pathname]);

  // Global click listener
  useEffect(() => {
    if (!enabled) return;
    const path = location.pathname;
    const onClick = (e) => {
      const el = e.target?.closest?.("button, a, [role='button'], [data-testid]");
      if (!el) return;
      const label =
        el.getAttribute("data-testid") ||
        el.getAttribute("aria-label") ||
        el.textContent?.trim().slice(0, 80) ||
        el.tagName.toLowerCase();
      endpoints.trackEvent({ type: "click", path, label }).catch(() => {});
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [enabled, location.pathname]);
}
